import {
  Button, Col, Form, Input, Modal, Row, Select, message,
} from 'antd';
import PropTypes from 'prop-types';
import React, {useState} from 'react';

import {RQUserRegistrationRole} from '../../../../../ReactQuery/apis/rqAuthAPI';

export const EMPLOYEE_ROLES = [
  {label: 'Doctor', value: 'DOCTOR'},
  {label: 'Therapist', value: 'THERAPIST'},
  // {label: 'Accountant', value: 'ACCOUNTANT'},
  // {label: 'Pharmacist', value: 'PHARMACIST'},
  {label: 'Receptionist', value: 'RECEPTIONIST'},
  {label: 'Admin', value: 'ADMIN'},
];

const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

export const FIELD_DEFS = {
  specialization: {
    label: 'Specialization',
    placeholder: 'e.g. Cardiologist',
    required: true,
  },
  licenseNumber: {
    label: 'License Number',
    placeholder: 'e.g. PH123456',
    required: true,
  },
  medicalname: {
    label: 'Medical / Store Name',
    placeholder: 'e.g. Apollo Pharmacy',
    required: true,
  },
  availability: {
    label: 'Availability',
    placeholder: 'e.g. 09:00 AM - 05:00 PM',
    required: true,
  },
  workExperience: {
    label: 'Work Experience',
    placeholder: 'e.g. 5 Years',
    required: true,
  },
  qualification: {
    label: 'Qualification',
    placeholder: 'e.g. MBBS, MD',
    required: true,
  },
};

export const ROLE_FIELDS = {
  DOCTOR: ['specialization', 'availability', 'workExperience', 'qualification'],
  THERAPIST: ['specialization', 'availability', 'workExperience', 'qualification'],
  // PHARMACIST: ['medicalname', 'licenseNumber', 'availability', 'qualification', 'workExperience'],
  RECEPTIONIST: ['availability', 'qualification', 'workExperience'],
  // ACCOUNTANT: ['availability', 'qualification', 'workExperience'],
  ADMIN: [

  ],
};

const COMMON_FIELDS = ['fullName', 'email', 'password', 'mobileNumber', 'gender', 'address'];

export const EmployeeRegistration = ({open, onClose, onSuccess}) => {
  const [form] = Form.useForm();
  const [selectedRole, setSelectedRole] = useState(null);

  const extraFields = ROLE_FIELDS[selectedRole] || [];

  const registerEmployee = RQUserRegistrationRole(selectedRole, {
    onSuccess: () => {
      message.success(`Employee registered successfully as ${selectedRole}`);
      form.resetFields();
      setSelectedRole(null);
      onSuccess?.();
      onClose();
    },
    onError: err => {
      const apiMessage = err?.response?.data?.message;
      message.error(apiMessage || err?.message || 'Registration failed');
    },
  });

  const handleFinish = values => {
    const {role, ...rest} = values;

    const allowedKeys = [...COMMON_FIELDS, ...extraFields];
    const payload = allowedKeys.reduce((acc, key) => {
      if (rest[key] !== undefined && rest[key] !== '') {
        acc[key] = rest[key];
      }
      return acc;
    }, {});

    registerEmployee.mutate(payload);
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedRole(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      maskClosable={false}
      onCancel={handleCancel}
      footer={null}
      title={(
        <span className={'text-primary-700 font-bold text-base'}>
          Register Employee
        </span>
      )}
      width={720}
      destroyOnHidden
    >
      <Form
        form={form}
        layout={'vertical'}
        onFinish={handleFinish}
        className={'mt-4'}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={'Role'}
              name={'role'}
              rules={[{required: true, message: 'Please select a role'}]}
            >
              <Select
                placeholder={'Select employee role'}
                options={EMPLOYEE_ROLES}
                onChange={value => setSelectedRole(value)}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Full Name'}
              name={'fullName'}
              rules={[
                {
                  required: true,
                  message: 'Please enter full name',
                },
                {
                  validator: (_, value) => {
                    if (!value) {return Promise.resolve();}

                    if (value.startsWith(' ')) {
                      return Promise.reject(
                        new Error('First character cannot be a space'),
                      );
                    }

                    if (!/^[A-Za-z ]+$/.test(value)) {
                      return Promise.reject(
                        new Error('Full name can contain only letters and spaces'),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder={'e.g. John Doe'}
                onKeyPress={e => {
                  if (!/[A-Za-z ]/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={'Email'}
              name={'email'}
              rules={[
                {required:false, message: 'Please enter email'},
                {type: 'email', message: 'Enter a valid email'},
              ]}
            >
              <Input placeholder={'e.g. john@example.com'} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Password'}
              name={'password'}
              rules={[
                {required:false, message: 'Please enter password'},
                {min: 6, message: 'Password must be at least 6 characters'},
              ]}
            >
              <Input.Password placeholder={'Enter password'} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={'Mobile Number'}
              name={'mobileNumber'}
              rules={[
                {required:false, message: 'Please enter mobile number'},
                {pattern: /^[0-9]{10}$/, message: 'Enter a valid 10-digit number'},
              ]}
            >
              <Input placeholder={'e.g. 9876543210'} maxLength={10} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={'Gender'}
              name={'gender'}
              rules={[{required:false, message: 'Please select gender'}]}
            >
              <Select
                placeholder={'Select gender'}
                options={GENDER_OPTIONS.map(g => ({value: g, label: g}))}
              />
            </Form.Item>
          </Col>
          <Col span={12} />
        
          {extraFields.map(fieldKey => {
            const def = FIELD_DEFS[fieldKey];
            return (
              <Col span={12} key={fieldKey}>
                <Form.Item
                  label={def.label}
                  name={fieldKey}
                  rules={def.required ? [{required: true, message: `Please enter ${def.label.toLowerCase()}`}] : []}
                >
                  <Input placeholder={def.placeholder} />
                </Form.Item>
              </Col>
            );
          })}

          <Col span={24}>
            <Form.Item
              label={'Address'}
              name={'address'}
              rules={[{required:false, message: 'Please enter address'}]}
            >
              <Input.TextArea rows={3} placeholder={'Enter address'} />
            </Form.Item>
          </Col>
        </Row>

        <div className={'flex justify-end gap-2 mt-2'}>
          <Button onClick={handleCancel} className={'btn-secondary'}>
            Cancel
          </Button>
          <Button
            type={'primary'}
            htmlType={'submit'}
            loading={registerEmployee.isPending}
            className={'btn-primary'}
          >
            Register
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

EmployeeRegistration.propTypes = {
  open:      PropTypes.bool.isRequired,
  onClose:   PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default EmployeeRegistration;
